import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import VideoBox from "../../casinoComponents/videoBox";
import MyBet from "../../casinoComponents/myBet";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import TrapCardsOverlay from "../../casinoComponents/videoBox/overlays/TrapCardsOverlay";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { getCasinoGameDetails, getMatchExposure, getMyBets } from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { formatBetNation, formatBetOdds } from "../../../utils/casinoMyBets";
import BetTableTrapRef from "./BetTableTrapRef";
import TrapRefResults from "./TrapRefResults";
import "../../../styles/games/trap.css";

// Reference game-header title (lastresults2 res1.cname)
const TRAP_TITLE = "The Trap";

/**
 * The Trap (casino/trap) in the reference shell: .casino-table.trap >
 *   .game-header + .casino-video (A / B cards with totals) + .casino-detail
 *   (Player A/B, LOW-7-HIGH card, J Q K card, number strip, remark, Last Result).
 */
export default function Trap() {
  const [gameData, setGameData] = useState({});
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${extractCasinoGame(pathname)}`;

  useEffect(() => {
    const name = extractCasinoGame(pathname);

    const fetchData = async () => {
      try {
        const response = await getCasinoGameDetails(name);
        setGameData(response || {});
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const matchId = gameData?.data?.data?.mid?.toString() || gameData?.data?.mid?.toString() || gameData?.mid?.toString();

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase().trim()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMyBets(userId, matchId);
        if (response.success && response.bets) {
          const formattedBets = response.bets.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.userName ?? bet.username,
            placeDate: bet.placeDate ?? bet.created_at,
            matchedBet: bet.player_name || bet.selection || "",
            odds: bet.odds || "0",
            stake: bet.stake || "0",
            type: bet.type || null,
            selection: bet.selection || bet.player_name || "",
            exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0
          }));
          setMyBets(formattedBets);
        }
      }
    } catch (error) {
      console.error('Error fetching my bets:', error);
    }
  }, [matchId]);

  useEffect(() => {
    if (matchId) {
      const refresh = () => {
        fetchExposure();
        fetchMyBetsData();
      };
      const firstId = setTimeout(refresh, 0);
      const exposureInterval = setInterval(refresh, 2000);
      return () => {
        clearTimeout(firstId);
        clearInterval(exposureInterval);
      };
    }
  }, [matchId, fetchExposure, fetchMyBetsData]);

  // Admin panel places no bets (the old PlaceBet was the NoBet stub): an odds click refreshes book + bets
  const handleOdds = (m, value) => {
    if (!value || String(m?.gstatus || "").toUpperCase() !== "OPEN") return;
    fetchExposure();
    fetchMyBetsData();
  };

  const feed = gameData?.data?.data ?? {};
  const roundId = feed?.mid || gameData?.data?.mid || gameData?.mid || "";
  const gameName = extractCasinoGame(pathname) || "trap";
  // "Player A-Main" / "Card 1 - JQK"
  const formatNation = (nat, bet) => formatBetNation(gameName, nat, bet);
  const formatRate = (bet) => formatBetOdds(gameName, bet, bet.odds ?? bet.userrate);
  const resultData = gameData?.lrs ?? feed?.lrs ?? [];

  return (
    <CasinoTableLayout tableClass="trap" rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} formatNation={formatNation} formatRate={formatRate} />}>
      <>
        <CasinoHeading name={TRAP_TITLE} roundId={roundId} />{" "}
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={feed?.lt}
          gameType={gameName}
          cardsOverlay={feed?.card != null && String(feed.card).length ? <TrapCardsOverlay card={feed.card} /> : null}
        />{" "}
        {feed?.mid ? (
          <div className="casino-detail">
            <BetTableTrapRef sub={feed?.sub} exposures={exposures} onOdds={handleOdds} />{" "}
            {feed?.remark ? (
              <div className="casino-remark mt-1">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
                </div>{" "}
                <marquee>{feed.remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType={gameName}
              data={resultData}
              gameData={gameData}
              viewAllLink={`/admin/reports/casinoresult/${gameName}`}
              resultComponent={<TrapRefResults gameType={gameName} mid={feed?.mid} fallbackTitle={TRAP_TITLE} />}
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
        formatNation={formatNation}
        formatRate={formatRate}
      />
    </CasinoTableLayout>
  );
}
